import * as React from 'react'
import { useMemo, useState } from 'react'

import { getUnixTime } from 'date-fns'

import {
  ChannelIcon,
  PlusIcon,
  Text,
  UnstyledButton,
} from '@buffer-mono/popcorn'

import { DateSeparator } from '~publish/components/DateSeparator'
import { PostingTimeline } from '~publish/components/PostingTimeline'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import type { Service } from '~publish/contexts/AccountContext/graphqlEnums'
import { getChannelName } from '~publish/helpers/channels'
import { trackEmptyStateClicked } from '~publish/helpers/queueCalendarTracking'
import { useCta } from '~publish/hooks/useCta'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { TrackRender } from '~publish/hooks/tracking'
import { useTimezone } from '~publish/hooks/useTimezone'

import {
  buildConnectedQueueSlots,
  buildPreviewQueueSlots,
  MAX_EXPANDED_QUEUE_SLOTS,
  MAX_VISIBLE_QUEUE_SLOTS,
} from './buildValueLedQueueSlots'
import type {
  ValueLedQueueChannel,
  ValueLedQueueSlot,
} from './buildValueLedQueueSlots'

import styles from './ValueLedQueueEmptyState.module.css'

type QueueSlotButtonProps = ValueLedQueueSlot & {
  buttonAttributes?: Omit<
    React.ComponentProps<typeof UnstyledButton>,
    'children' | 'className' | 'onClick'
  >
  onClick: (
    slot: ValueLedQueueSlot,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void
}

function QueueSlotButton({
  id,
  date,
  channelId,
  channelName,
  channelService,
  buttonAttributes,
  onClick,
}: QueueSlotButtonProps): JSX.Element {
  const dateTimeFormatter = useDateTimeFormatter()

  const channelLabel = channelId
    ? `${channelName} on ${getChannelName(channelService)}`
    : getChannelName(channelService)

  return (
    <UnstyledButton
      {...buttonAttributes}
      className={styles.slot}
      aria-label={`Add a post to ${channelLabel} at ${dateTimeFormatter(date)}`}
      data-datetime={date}
      data-testid="queue-slot"
      onClick={(event): void =>
        onClick({ id, date, channelId, channelName, channelService }, event)
      }
    >
      <ChannelIcon
        type={channelService}
        color="branded"
        size={16}
        aria-hidden
      />
      <PlusIcon />
      New
    </UnstyledButton>
  )
}

export function ValueLedQueueSlots({
  previewSlots,
  expandedSlots,
  buttonAttributes,
  onSlotClick,
}: {
  previewSlots: ValueLedQueueSlot[]
  expandedSlots: ValueLedQueueSlot[]
  buttonAttributes?: Omit<
    React.ComponentProps<typeof UnstyledButton>,
    'children' | 'className' | 'onClick'
  >
  onSlotClick: (
    slot: ValueLedQueueSlot,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void
}): JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const slots = expanded ? expandedSlots : previewSlots

  return (
    <PostingTimeline role="feed" data-testid="value-led-queue-empty-state">
      {slots.map((slot, index) => (
        <React.Fragment key={slot.id}>
          <DateSeparator
            currentDate={slot.date}
            previousDate={index > 0 ? slots[index - 1]?.date : undefined}
          />
          <PostingTimeline.Entry>
            <PostingTimeline.TimeLabel date={slot.date} />
            <PostingTimeline.Content>
              <QueueSlotButton
                {...slot}
                buttonAttributes={buttonAttributes}
                onClick={onSlotClick}
              />
            </PostingTimeline.Content>
          </PostingTimeline.Entry>
        </React.Fragment>
      ))}

      {!expanded && expandedSlots.length > previewSlots.length && (
        <div className={styles.showMoreRecommendedSlots}>
          <UnstyledButton
            className={styles.showMoreRecommendedSlotsButton}
            onClick={(): void => setExpanded(true)}
          >
            <PlusIcon color="subtle" />
            <Text color="subtle">More Recommended Times</Text>
          </UnstyledButton>
        </div>
      )}
    </PostingTimeline>
  )
}

export function ValueLedQueueEmptyState({
  channels,
  onboardingServices,
  fallback,
}: {
  channels: ValueLedQueueChannel[]
  onboardingServices: Service[] | null
  fallback: React.ReactNode
}): JSX.Element {
  const timezone = useTimezone()
  const organizationId = useOrganizationId()
  const {
    triggerAttributes,
    createNewPinnedPostInComposer,
    createNewPostInComposer,
  } = usePostComposer()
  const cta =
    channels.length > 0
      ? 'publish-allChannels-valueLedQueue-slot-1'
      : 'publish-allChannels-valueLedQueue-previewSlot-1'
  const { clicked: trackClick } = useCta(cta)

  const { previewSlots, expandedSlots } = useMemo(() => {
    const buildSlots = (limit: number): ValueLedQueueSlot[] =>
      channels.length > 0
        ? buildConnectedQueueSlots({ channels, limit })
        : buildPreviewQueueSlots({ onboardingServices, timezone, limit })

    return {
      previewSlots: buildSlots(MAX_VISIBLE_QUEUE_SLOTS),
      expandedSlots: buildSlots(MAX_EXPANDED_QUEUE_SLOTS),
    }
  }, [channels, onboardingServices, timezone])

  if (previewSlots.length === 0) {
    return <>{fallback}</>
  }

  const handleSlotClick = (
    slot: ValueLedQueueSlot,
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.preventDefault()
    trackClick()
    if (organizationId) {
      trackEmptyStateClicked({
        organizationId,
        feature: 'queue',
        channel: slot.channelService,
      })
    }
    const dueAt = getUnixTime(slot.date)

    if (slot.channelId) {
      void createNewPinnedPostInComposer({
        cta,
        channels: [slot.channelId],
        prefillPostData: {
          dueAt,
          pinned: true,
        },
      })
      return
    }

    void createNewPostInComposer({
      cta,
      channels: [],
      prefillPostData: {
        dueAt,
        pinned: true,
      },
    })
  }

  return (
    <>
      <TrackRender
        componentName="ValueLedQueueEmptyState"
        organizationId={organizationId}
        product="publish"
        platform="new_publish"
      />
      <ValueLedQueueSlots
        previewSlots={previewSlots}
        expandedSlots={expandedSlots}
        buttonAttributes={triggerAttributes}
        onSlotClick={handleSlotClick}
      />
    </>
  )
}
